<template>


  <div class="wrapper" v-if="notificationsData?.items?.length">

    <div class="list" role="list" ref="listRef">
         
      <!-- Notification groups with time grouping -->
      <UiTimeGroupedList>
        <NotificationV2NotificationCard
          v-if="NotificationChannel.Direct === currentChannel || NotificationChannel.Community === currentChannel"
          v-for="(notification, index) in notificationsData.items"
          :key="index"
          :notification="notification"
          :show-template="true"
          @mark-read="markAsRead"
        /> 

        <NotificationV2NotificationGroupCard
          v-else-if="NotificationChannel.Following === currentChannel"
          v-for="(group, index) in notificationsData.items"
          :key="group.group"
          :group="group"
          :channel="currentChannel"
          :group-index="index"
          @mark-child-read="markGroupChildAsRead"
          @mark-group-read="markGroupAsRead"
          @load-more-children="loadMoreGroupItems(index)"
        />
      </UiTimeGroupedList>
 
    </div>

    <ClientOnly>
      <UiInfinityScroll 
        :is-loading="notificationsDataStatus !== 'pending' && !isEnded"
        ref="elInfinityScroll"/>
    </ClientOnly>
        
  </div>


  <template
      v-if="!notificationsData && notificationsDataStatus === 'pending'"
    >
      <div class="gloading">

      </div>
    </template>

  <UiEmpty
      v-if="notificationsData?.items?.length === 0 && notificationsDataStatus === 'success'"
      text="No notifications yet"
    />
</template>

<script setup lang="ts">

const { $api } = useNuxtApp()

import { useIntersectionObserver } from '@vueuse/core'
import { watchArray } from '@vueuse/core'

import { ref, computed, onMounted, onUnmounted } from 'vue'

import { useNotifications } from '@/composables/useNotifications'
import type {NotificationGroup } from '@/types/notification'
import { NotificationChannel } from '@/types/notification'

const props = defineProps<{ 
  channel: NotificationChannel
  autoLoad?: boolean
}>()

const emit = defineEmits<{
  (e: 'initialized'): void
}>()

const elInfinityScroll = ref(null)
const start = ref<number>(0)
const limit = ref<number>(20)
const isEnded = ref<boolean>(false)
const notificationsV2 = useNotifications()
const listRef = ref<HTMLElement>()
const loadMoreTrigger = ref<HTMLElement>()

// Current data for this type
// const groups = computed(() => notificationsV2.notificationGroups.value[props.channel])
const loading = computed(() => notificationsV2.loadingStates.value[props.channel])
const hasMore = computed(() => notificationsV2.paginationStates.value[props.channel].hasMore)
const currentChannel = computed(() => props.channel)
 


const markGroupAsRead = (id: string, channel: NotificationChannel, cnt: number) => {  
  useNotifications().markGroupAsRead(id, channel, cnt)
  notificationsData.value.items = notificationsData.value.items.filter((item: any) => item.id !== id)
}

const markGroupChildAsRead = (id: string, channel: NotificationChannel) => {  
  useNotifications().markAsRead(id, channel)
  notificationsData.value.items.forEach((item: any) => {
    item.items_notification = item.items_notification.filter((childItem: any) => {
      return childItem.id !== id
    })
  })
  notificationsData.value.items = notificationsData.value.items.filter((item: any) => item.items_notification.length > 0)
}

const markAsRead = (id: string, channel: NotificationChannel) => {
  useNotifications().markAsRead(id, channel)
  notificationsData.value.items = notificationsData.value.items.filter((item: any) => item.id !== id)
}

// Function to clear all notifications in the list
const markAllAsReadAndClear = async () => {
  notificationsData.value.items = []
}

// Expose function for parent components
defineExpose({
  markAllAsReadAndClear
})




const loadMoreGroupItems = (groupIndex: number) => {
  notificationsV2.loadMoreGroupItems(props.channel, groupIndex)
}



 

const loadData = async function() {  
  
  if(NotificationChannel.Direct === props.channel) {
    return await $api.getNotificationsV2(useAuth().getId(), NotificationChannel.Direct, start.value, limit.value)
  } else if(NotificationChannel.Following === props.channel) {
    return await $api.getNotificationsV2Groups(useAuth().getId(), NotificationChannel.Following, start.value, limit.value)
  } else if(NotificationChannel.Community === props.channel) {
    return await $api.getNotificationsV2(useAuth().getId(), NotificationChannel.Community, start.value, limit.value)
  } else {
    return [];
  }
}

const { status: notificationsDataStatus, refresh: refreshData, data: notificationsData } = await useLazyAsyncData('notificationsData' + props.channel, async () => await loadData())




watchArray([start], async ([newStart], added, removed) => {
  console.log('watcher');

  if(newStart.value == 0){
    notificationsData.value.items = [];
    isEnded.value = false;
  }
  
  notificationsDataStatus.value = 'pending';

  var dt = await loadData();

  if(dt?.items.length == 0)
    isEnded.value = true;
  
  notificationsData.value.items = [...notificationsData.value.items, ...dt.items]  
  notificationsDataStatus.value = 'success';

}, {deep: true})




useIntersectionObserver(
  elInfinityScroll,
  ([{isIntersecting}]) => {
    if(isIntersecting){
      console.log('infinity scroll event');        
      if(notificationsDataStatus.value === 'success' && !isEnded.value)
        start.value = start.value + limit.value;
    }
  },
  { distance: 10 }
)

</script>

<style scoped>
.wrapper{ position:relative; flex:1 1 auto; overflow:hidden; }
.list{ height:100%; overflow-y:auto; padding:8px 4px; }
/* Time grouping styles are now handled in UiTimeGroupedList */
/* Fade shadows top/bottom */
.list::before,
.list::after{ content:''; position:sticky; left:0; right:0; height:12px; pointer-events:none; z-index:1; }
.list::before{ top:0; background:linear-gradient(to bottom,var(--bg-layer0),transparent); }
.list::after{ bottom:0; background:linear-gradient(to top,var(--bg-layer0),transparent); }

/* Loading skeleton */
.loading-skeleton{ padding:8px 0; }
.skeleton-item{ display:flex; align-items:center; gap:12px; padding:8px 12px; margin-bottom:8px; }
.skeleton-avatar{ width:32px; height:32px; border-radius:50%; background:var(--bg-hover); animation:pulse 1.5s ease-in-out infinite; }
.skeleton-content{ flex:1; }
.skeleton-title{ height:14px; width:70%; background:var(--bg-hover); border-radius:4px; margin-bottom:4px; animation:pulse 1.5s ease-in-out infinite; }
.skeleton-message{ height:12px; width:50%; background:var(--bg-hover); border-radius:4px; animation:pulse 1.5s ease-in-out infinite; }

/* Load more */
.load-more-trigger{ text-align:center; padding:16px; }
.loading-more{ display:flex; justify-content:center; padding:16px; }
.loading-spinner i{ font-size:16px; animation:spin 1s linear infinite; }

/* Animations */
@keyframes pulse{ 0%,100%{opacity:1;} 50%{opacity:0.5;} }
@keyframes spin{ from{transform:rotate(0deg);} to{transform:rotate(360deg);} }

/* Slide-in animation for new items */
.slide-enter-from{ opacity:0; transform:translateY(4px); }
.slide-enter-active{ transition:opacity 150ms ease,transform 150ms ease; }
.slide-enter-to{ opacity:1; transform:translateY(0); }
.slide-leave-from{ opacity:1; transform:translateY(0); }
.slide-leave-active{ transition:opacity 150ms ease,transform 150ms ease; }
.slide-leave-to{ opacity:0; transform:translateY(-4px); }
</style> 